import {
  AbstractControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { TuiDay, tuiHexToRgb, TuiTime } from '@taiga-ui/cdk';
import { format } from 'date-fns-tz';
import { parse } from 'date-fns';
import { EMPTY, fromEvent, MonoTypeOperatorFunction, Observable, throwError, timer } from 'rxjs';
import { map, mergeMap, take, tap } from 'rxjs/operators';
import { tuiFormatNumber } from '@taiga-ui/core';
import { tuiGetCurrencySymbol } from '@taiga-ui/addon-commerce';
import { ChangeDetectorRef } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { TimeZoneInfo } from '@fb-tool-front/shared/types/types';
import dayjs from 'dayjs';
import objectSupport from 'dayjs/plugin/objectSupport';

dayjs.extend(objectSupport);

declare global {
  namespace Intl {
    type Key = 'calendar' | 'collation' | 'currency' | 'numberingSystem' | 'timeZone' | 'unit';

    function supportedValuesOf(input: Key): string[];
  }
}

export const cloneObject = (obj: any): any => {
  const clone = Array.isArray(obj) ? [] : {};
  Object.keys(obj).forEach(i => {
    clone[i] = obj[i] !== null && typeof obj[i] === 'object' ? cloneObject(obj[i]) : obj[i];
  });
  return clone;
};

export const checkHex = (hex: string): boolean => {
  return /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2}|)$/i.exec(hex) ? true : false;
};

export const formatHexColor = (hex: string): string => {
  if (checkHex(hex)) {
    if (hex[0] === '#') {
      return hex.slice(1);
    }
    return hex;
  }
  return 'FFFFFF';
};

/**
 * Deep clones the given AbstractControl, preserving values, validators, async validators, and disabled status.
 * @param control AbstractControl
 * @returns AbstractControl
 */
export function cloneAbstractControl<T extends AbstractControl>(control: T): T {
  let newControl: T;

  if (control instanceof UntypedFormGroup) {
    const formGroup = new UntypedFormGroup({}, control.validator, control.asyncValidator);
    const controls = control.controls;

    Object.keys(controls).forEach(key => {
      formGroup.addControl(key, cloneAbstractControl(controls[key]));
    });

    newControl = formGroup as any;
  } else if (control instanceof UntypedFormArray) {
    const formArray = new UntypedFormArray([], control.validator, control.asyncValidator);

    control.controls.forEach(formControl => formArray.push(cloneAbstractControl(formControl)));

    newControl = formArray as any;
  } else if (control instanceof UntypedFormControl) {
    newControl = new UntypedFormControl(
      control.value,
      control.validator,
      control.asyncValidator
    ) as any;
  } else {
    throw new Error('Error: unexpected control value');
  }

  if (control.disabled) newControl.disable({ emitEvent: false });

  return newControl;
}

/* Get the greatest common divisor*/
export const gdc = (a: number, b: number): number => {
  return b === 0 ? a : gdc(b, a % b);
};

export const validateMultipleEmails = (control: AbstractControl) => {
  const emails = control.value;

  if (!emails) {
    return null;
  }

  const hasIncorrectEmails = emails.some((email: string) =>
    Validators.email(new UntypedFormControl(email))
  );
  return hasIncorrectEmails ? { email: true } : null;
};

export function audioDurationValidator(val: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value < val) {
      return { audioDurationValidator: { requiredValue: val } };
    }

    return null;
  };
}

export function randomIntFromInterval(min, max) {
  return Math.floor(Math.random() * (max - min + 1) + min);
}

export function serverStringToDate(
  date: string,
  format: string = `yyyy-MM-dd'T'HH:mm:ssxxxx`
): Date {
  if (!date) return null;
  let nativeDate = null;
  try {
    nativeDate = parse(date, format, new Date());
  } catch (err) {}

  return nativeDate;
}

export function serverStringToTuiDay(date: string): TuiDay {
  if (!date) return null;

  const nativeDate = serverStringToDate(date);
  return nativeDate ? dateToTuiDay(nativeDate) : null;
}

export function tuiDayToDate(date: TuiDay | [TuiDay, TuiTime]): Date {
  if (!date) return null;
  if (Array.isArray(date)) {
    let [d, t] = date;
    return new Date(d.year, d.month, d.day, t.hours, t.minutes);
  }
  return new Date(date.year, date.month, date.day);
}

export function tuiDayToServerString(date: TuiDay): string {
  const nativeDate = tuiDayToDate(date);
  return nativeDate ? dateToServerString(nativeDate) : null;
}

export function dateToServerString(
  date: Date,
  timeZone?: string,
  dateFormat = `yyyy-MM-dd'T'HH:mm:ssxxxx`
): string {
  if (!date) return null;
  return format(date, dateFormat, { timeZone });
}

export function dateToTuiDay(date: Date): TuiDay {
  if (!date) return null;
  return new TuiDay(date.getFullYear(), date.getMonth(), date.getDate());
}

export function dateToTuiTime(date: Date): TuiTime {
  if (!date) return null;
  return new TuiTime(date.getHours(), date.getMinutes());
}

export function unixToTuiDate(timestamp: number | string): [TuiDay, TuiTime] {
  let date = new Date(timestamp);
  return [dateToTuiDay(date), dateToTuiTime(date)];
}

export function isNegativeZero(value) {
  return Object.is(value, -0);
}

export const genericRetryStrategy =
  ({
    maxRetryAttempts = 10,
    scalingDuration = 1000,
    excludedStatusCodes = []
  }: {
    maxRetryAttempts?: number;
    scalingDuration?: number;
    excludedStatusCodes?: number[];
  } = {}) =>
  (attempts: Observable<any>) => {
    return attempts.pipe(
      mergeMap((error, i) => {
        const retryAttempt = i + 1;
        // if maximum number of retries have been met
        // or response is a status code we don't wish to retry, emit empty
        if (retryAttempt > maxRetryAttempts || excludedStatusCodes.find(e => e === error.status)) {
          return throwError(error);
        }
        // retry after 1s, 2s, etc...
        return timer(retryAttempt * scalingDuration);
      })
    );
  };

export function getContrastColor(color: string) {
  const rgb = tuiHexToRgb(color);

  if (Math.round((rgb[0] * 259 + rgb[1] * 550 + rgb[2] * 114) / 1000) > 125) {
    return '#000000';
  } else {
    return '#ffffff';
  }
}

export function evaluateArithmeticOperator(a: number, b: number, operator: string): number {
  switch (operator) {
    case '+': {
      return a + b;
    }
    case '-': {
      return a - b;
    }
    case '*': {
      return a * b;
    }
    case '/': {
      return a / b;
    }
  }
}

export const getLocaleByTimezone = (timeZone: string): string => {
  return timeZone.includes('America') ? 'en' : 'ru';
};

export const numberToAbbreviatedString = (n: number): string => {
  if (n < 1e3) {
    return String(n);
  }
  if (n >= 1e3 && n < 1e6) {
    return +(n / 1e3).toFixed(1) + 'K';
  }
  if (n >= 1e6 && n < 1e9) {
    return +(n / 1e6).toFixed(1) + 'M';
  }
  if (n >= 1e9 && n < 1e12) {
    return +(n / 1e9).toFixed(1) + 'B';
  }
  if (n >= 1e12) {
    return +(n / 1e12).toFixed(1) + 'T';
  }
};

export const formatMoney = (value: number, currency: string, showCurrency = true): string => {
  const formattedValue = tuiFormatNumber(value, {
    decimalLimit: 2,
    decimalSeparator: '.',
    thousandSeparator: ','
  });

  const symbol = tuiGetCurrencySymbol(currency) ?? currency;

  return ['USD', 'EUR'].includes(currency)
    ? `${showCurrency ? symbol : ''}${formattedValue}`
    : `${formattedValue} ${showCurrency ? symbol : ''}`;
};

export function watch<T>(ref: ChangeDetectorRef): MonoTypeOperatorFunction<T> {
  return tap(() => ref.markForCheck());
}

export function getRouterParams(router: Router) {
  let params: Record<string, string> = {};

  const stack: ActivatedRouteSnapshot[] = [router.routerState.snapshot.root];

  while (stack.length > 0) {
    const route = stack.pop();
    if (route === undefined) {
      continue;
    }

    params = { ...params, ...route.params };

    stack.push(...route.children);
  }

  return params;
}

export function getCurrentUrl(router: Router) {
  let url = '';

  const stack: ActivatedRouteSnapshot[] = [router.routerState.snapshot.root];

  while (stack.length > 0) {
    const route = stack.pop();

    if (route === undefined) {
      continue;
    }

    url = route.url[0]?.path;

    stack.push(...route.children);
  }

  return url;
}

export function getDatesObject(
  dates: [string, TuiDay | [TuiDay, TuiTime]][],
  timeZone?: TimeZoneInfo,
  dateFormat = null
): {
  [key: string]: string;
} {
  return dates
    .map(([key, value]): [string, Date] => {
      let dateWithTime = tuiDayToDate(value);

      if (value) {
        if (key === 'date_start' && value instanceof TuiDay) {
          dateWithTime.setHours(0, 0, 0);
        }

        if (key === 'date_stop' && value instanceof TuiDay) {
          dateWithTime.setHours(23, 59, 59);
        }
      }

      return [key, dateWithTime];
    })
    .map(([key, value]) => ({ [key]: dateToServerString(value, timeZone?.id, dateFormat) }))
    .reduce(
      (prev, curr) => ({
        ...prev,
        ...curr
      }),
      {}
    );
}

export function atLeastOne(...fields: string[]) {
  return (fg: FormGroup): ValidationErrors | null => {
    return fields.some(fieldName => Boolean(fg.get(fieldName)?.value))
      ? null
      : ({ atLeastOne: 'At least one field has to be provided.' } as ValidationErrors);
  };
}

export const dateRangeValidator = (control: AbstractControl): ValidationErrors | null => {
  let dateStart: TuiDay | [TuiDay, TuiTime] = control.get('date_start').value;
  let dateStop: TuiDay | [TuiDay, TuiTime] = control.get('date_stop').value;
  let isStopBeforeStart = false;

  if (!dateStart || !dateStop) {
    return null;
  }

  if (Array.isArray(dateStart) && Array.isArray(dateStop)) {
    let [dayStart, timeStart] = dateStart;
    let [dayStop, timeStop] = dateStop;

    isStopBeforeStart = dayjs({
      year: dayStop.year,
      month: dayStop.month,
      day: dayStop.day,
      hour: timeStop?.hours,
      minute: timeStop?.minutes
    }).isBefore(
      dayjs({
        year: dayStart.year,
        month: dayStart.month,
        day: dayStart.day,
        hour: timeStart?.hours,
        minute: timeStart?.minutes
      })
    );
  } else if (dateStart instanceof TuiDay && dateStop instanceof TuiDay) {
    isStopBeforeStart = dayjs({
      year: dateStop.year,
      month: dateStop.month,
      day: dateStop.day
    }).isBefore(
      dayjs({
        year: dateStart.year,
        month: dateStart.month,
        day: dateStart.day
      })
    );
  }

  return isStopBeforeStart ? { other: `Start date can't be in the past` } : null;
};

export function getTimeZoneInfo(): TimeZoneInfo[] {
  const supportedTimeZones = Intl.supportedValuesOf('timeZone');
  const timeZoneMap = new Map<string, TimeZoneInfo>();

  supportedTimeZones.forEach(tz => {
    const offsetFormat = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'longOffset'
    });

    const formattedOffset = offsetFormat.format(new Date());
    const [, offsetString] = formattedOffset.split(', ');

    timeZoneMap.set(offsetString, {
      title: offsetString,
      id: offsetString.replace(/GMT(.*)/, (match, timeZone) => timeZone.replace(':', '') || '+0000')
    });
  });

  return [...timeZoneMap.values()].sort((a, b) => parseInt(a.id) - parseInt(b.id));
}

export function getImageSize$(url: string): Observable<{ width: number; height: number }> {
  const image = new Image();

  image.src = url;

  return fromEvent(image, 'load').pipe(
    take(1),
    map(event => {
      const img = event.target as HTMLImageElement;

      return {
        width: img.width,
        height: img.height
      };
    })
  );
}

export function getNestedProp(obj: object, path: string, delimiter: string) {
  if (typeof path !== 'string' || path.length === 0 || typeof obj !== 'object' || obj == null) {
    return obj;
  }

  path = path.trim();

  const props = path.split(delimiter);

  if (props.length > 1) {
    return getNestedProp(obj[props[0]], props.slice(1).join(delimiter), delimiter);
  } else if (props.length == 1) {
    return obj[props[0]];
  }
}

export function diff<T, K>(arr1: T[], arr2: K[], compare: (a: T, b: K) => boolean): T[] {
  return arr1.filter(item1 => !arr2.some(item2 => compare(item1, item2)));
}
